<template>
  <div>
    <md-dialog :md-active.sync="selectedFile.showing">
      <md-dialog-title>Comments on file.</md-dialog-title>
      <md-dialog-content>
        <div class="md-layout-item">
          <md-field>
            <label>Add a comment to the file (optional)</label>
            <md-input type="text" v-model="selectedFile.comment" />
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-danger" :disabled="selectedFile.uploading" @click="sendFile">
          <span v-if="!selectedFile.uploading">Ok</span>
          <span v-else>Uploading...</span>
        </md-button>
      </md-dialog-actions>
    </md-dialog>
    <md-dialog :md-active.sync="deletableFile.trying">
      <md-dialog-title>Delete file</md-dialog-title>
      <md-dialog-content v-if="deletableFile.file.name">
        Are you sure you want to delete <b>{{ deletableFile.file.name }}</b>?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="deletableFile.deleting" @click.prevent="deletableFile.trying = false">Cancel</md-button>
        <md-button class="md-primary" :disabled="deletableFile.deleting" @click.prevent="deleteFile()">
          {{ !deletableFile.deleting ? "Delete" : "Deleting..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>
    <md-dialog-alert :md-active.sync="isQuestionnaireFinished" md-title="Questionnaire answered"
      md-content="Questionnaire answered successfully!" @md-closed="$router.push({ name: 'Reviews' })"
      @md-clicked-outside="$router.push({ name: 'Reviews' })" />
    <div class="md-layout" v-if="isFetching">
      <div class="md-layout-item md-size-67 md-xsmall-size-80 mx-auto text-center">
        <span class="md-title">Loading review and with answers (if any)...</span>
        <md-progress-spinner class="md-accent md-flex text-center" :md-stroke="3" md-mode="indeterminate">
        </md-progress-spinner>
      </div>
    </div>
    <div class="md-layout" v-else>
      <div class="md-layout-item md-size-67 md-xsmall-size-80 mx-auto">
        <md-card>
          <md-card-content>
            <div class="md-layout">
              <div class="md-layout-item md-size-50">
                <span class="md-body-2">Client Name:
                  <span class="md-body-1">{{ client_name }}</span></span>
              </div>
              <div class="md-layout-item md-size-50">
                <span class="md-body-2">Project/Job Code:
                  <span class="md-body-1">{{ project }}</span></span>
              </div>
              <div class="md-layout-item md-size-50">
                <span class="md-body-2">Description of Engagement:
                  <span class="md-body-1">{{
                    engagement_description
                    }}</span></span>
              </div>
              <div class="md-layout-item md-size-50">
                <span class="md-body-2">Engagement Leader:
                  <span class="md-body-1">{{ engagement_leader }}</span></span>
              </div>
              <div class="md-layout-item md-size-50">
                <span class="md-body-2">Manager:
                  <span class="md-body-1">{{ manager_name }}</span></span>
              </div>
            </div>
          </md-card-content>
        </md-card>

        <md-card>
          <md-card-content>
            <md-steppers v-show="questions.length > 0 && review.id" md-vertical :md-dynamic-height="true"
              @md-changed="setStepPosition" ref="answersStepper" :md-active-step="defaultActiveStep">
              <span>
                <md-step v-for="(qnst, i) in questions" :key="i" class="md-stepper-flex rating" :class="
                  {
                    ratings: review.ratings,
                    section: qnst.questions[0].section,
                    qnst: qnst,
                    showError: showError,
                    status: review.status,
                  } | ratingClass
                " :md-label="qnst.section_text" :md-description="qnst.section_description" :id="qnst.section_text"
                  @click.prevent="makeSectionsClosable($event)" v-show="isVisible(qnst.questions[0].section)">
                  <div class="md-layout md-layout-item" :class="
                    question.type === 3 ||
                      question.type === 5 ||
                      question.type === 6
                      ? 'md-size-50'
                      : 'md-size-100'
                  " v-for="(question, j) in qnst.questions" :key="j">
                    <single-choice :answered="questionError(question)" :question="question"
                      :answer="findAnswer(question.id)" :review="review" :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 1" />

                    <single-choice-long-form :answered="questionError(question)" :question="question"
                      :answer="findAnswer(question.id)" :review="review" :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 7" />

                    <file :answered="questionError(question)" :question="question"
                      :answers="findAnswer(question.id, true)" :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 2" />

                    <number :answered="questionError(question)" :question="question" :answer="findAnswer(question.id)"
                      :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 3 || question.type === 5" />

                    <open-answer :answered="questionError(question)" :question="question"
                      :answer="findAnswer(question.id)" :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 4" />

                    <date :answered="questionError(question)" :question="question" :answer="findAnswer(question.id)"
                      :is-disabled="
                        isDisabledForReviewer(question) ||
                        disabledForReviewerAnyTime ||
                        review.status === 10
                      " @post-sent="postSent($event)" v-if="question.type === 6" />
                  </div>
                  <div class="md-layout md-layout-item md-size-100">
                    <div class="md-layout mt-4" v-if="
                      findSection(qnst) !== 'engagement_leader' &&
                      findSection(qnst) !== 'manager' &&
                      findSection(qnst) !== 'engagement_leader_for_quality' &&
                      findSection(qnst) !== 'manager_for_quality' &&
                      findSection(qnst) !== 'engagement_leader_for_inFlight' &&
                      findSection(qnst) !== 'manager_for_inFlight' &&
                      findSection(qnst) !== 'review_comments'
                    ">
                      <div class="md-layout-item md-size-100">
                        <overall-compliance-rating :is-disabled="disabledForReviewerAnyTime || review.status === 10"
                          :rating="findRating(findSection(qnst))" :review="qnst" :section="findSection(qnst)"
                          :rqrReviewer="review.rqr_reviewer_id" @post-sent="postSent($event)" @updateRating="updateRating">
                        </overall-compliance-rating>
                      </div>
                    </div>
                  </div>
                </md-step>
              </span>
            </md-steppers>
            <br />

            <md-card v-if="
              isAdmin ||
              isReviewer ||
              isRqrLeader ||
              isAudit ||
              isAdminTI ||
              (isReviewerAnytime && review.status > 5)
            ">
              <md-card-content>
                <overall-compliance-rating :is-disabled="disabledForReviewerAnyTime || review.status === 10"
                  :rating="findRating('final')" :review="review" :section="'final'" :rqrReviewer="review.rqr_reviewer_id"
                  @post-sent="postSent($event)" @updateRating="updateRating">
                </overall-compliance-rating>
              </md-card-content>
            </md-card>

            <md-card v-if="
              isAdmin ||
              isLeader ||
              isReviewer ||
              isRqrLeader ||
              isAudit ||
              isAdminTI ||
              (isReviewerAnytime && review.status > 5)
            ">
              <md-card-content>
                You can append multiple documents as evidence for this review
                <md-table v-model="review.files" md-fixed-header class="report-compliance">
                  <md-table-toolbar>
                    <h1 class="md-title">Attachments</h1>
                  </md-table-toolbar>
                  <md-table-row slot="md-table-row" slot-scope="{ item: file, index: i }">
                    <md-table-cell class="md-size-30" md-label="File name">{{
                      file.name
                      }}</md-table-cell>
                    <md-table-cell class="md-size-50" md-label="Observation/Comment/Purpose">{{ file.comment }}
                    </md-table-cell>
                    <md-table-cell class="md-size-10" md-label="Actions" md-numeric>
                      <md-button class="md-simple md-just-icon" :md-ripple="false" @click.prevent="showFile(file)"
                        title="Show file">
                        <md-icon>search</md-icon>
                      </md-button>
                      <md-button v-if="
                        isAdmin || isLeaderAnytime || file.user_id === userId
                      " class="md-simple md-just-icon" :md-ripple="false" :disabled="!isAdmin && review.status === 10"
                        @click.prevent="tryToDeleteFile(file, i)" title="Remove file">
                        <md-icon>delete</md-icon>
                      </md-button>
                    </md-table-cell>
                  </md-table-row>
                  <md-table-empty-state md-icon="attach_file" md-description="No attachments for this review">
                  </md-table-empty-state>
                </md-table>
                <div class="file-input pull-right">
                  <div class="button-container">
                    <md-button class="md-default md-round md-fileinput md-danger"
                      :disabled="(!isAdmin && review.status === 10) || isAdminTI || isAudit || review.status === 10">
                      Append new attachment
                      <input type="file" @change="onFileSelected($event)" />
                    </md-button>
                  </div>
                </div>
              </md-card-content>
            </md-card>

            <div class="
                md-layout-item md-size-67 md-xsmall-size-80
                mx-auto
                text-center
                mb-2
              " v-if="
                (isLeaderAnytime && review.status !== 2) ||
                (isManagerAnytime && review.status !== 4) ||
                (isReviewerAnytime && review.status < 6) ||
                (isRqrLeaderAnytime && review.status < 6)
              ">
              <span class="md-title">
                The current review status is
                <i>{{ review.status | reviewStatus }}</i>, so it's possible that you are not allowed to answer it yet.
              </span>
            </div>
            <div class="md-layout pull-right" v-if="isReviewer || isRqrLeader">
              <p class="pull-right">
                There
                {{
                questionsAndRatingsTotalLeft > 0
                ? questionsAndRatingsTotalLeft === 1
                ? "is"
                : "are"
                : " are no"
                }}
                {{
                questionsAndRatingsTotalLeft > 0
                ? questionsAndRatingsTotalLeft
                : ""
                }}
                question{{ questionsAndRatingsTotalLeft === 1 ? "" : "s" }} left
                to be answered by reviewer or RQR leader.
                <span v-if="questionsAndRatingsTotalLeft > 0">Please, answer all questions.</span>
              </p>
            </div>
            <md-button class="md-danger pull-right" v-if="canSend() && review.status <= 9" :disabled="
              isSending
            " @click.prevent="sendAll()">
              {{ isSending ? "Sending..." : "Send" }}
            </md-button>
            <md-button class="pull-left" :disabled="isSending" @click="$router.go(-1)">
              Back
            </md-button>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "@/components";
import { reviewStatus, ratingClass } from "@/filters";
import { validateRequiredRatings } from "@/utils/reviewValidations";
import {
  SingleChoice,
  SingleChoiceLongForm,
  OpenAnswer,
  File,
  Number,
  Date,
} from "@/components/Questions";
import OverallComplianceRating from "./Reviewer/OverallComplianceRating.vue";
import { camelToSnake } from "@/methods/api/requests"


export default {
  components: {
    SingleChoice,
    SingleChoiceLongForm,
    OpenAnswer,
    File,
    Number,
    Date,
    OverallComplianceRating,
    Modal,
  },

  filters: {
    ratingClass,
    reviewStatus,
  },

  computed: {
    profile() {
      return this.$store.state.currentProfile;
    },

    userId() {
      return this.$store.state.user.id;
    },

    isAdmin() {
      return this.profile === "admin";
    },

    isAudit() {
      return this.profile === "audit";
    },

    isAdminTI() {
      return this.profile === "adminTi";
    },

    isManager() {
      return (
        this.review.manager_engagement_id === this.userId &&
        this.profile === "manager" &&
        this.review.status === 4
      );
    },

    isManagerAnytime() {
      return (
        this.review.manager_engagement_id === this.userId &&
        this.profile === "manager"
      );
    },

    isReviewer() {
      return (
        this.review.rqr_reviewer_id === this.userId &&
        this.profile === "reviewer" &&
        this.review.status === 6
      );
    },

    isReviewerAnytime() {
      return (
        this.review.rqr_reviewer_id === this.userId && this.profile === "reviewer"
      );
    },

    isLeader() {
      return (
        this.review.engagement_leader_id === this.userId &&
        this.profile === "engagement_leader" &&
        this.review.status === 2
      );
    },

    isLeaderAnytime() {
      return (
        this.review.engagement_leader_id === this.userId &&
        this.profile === "engagement_leader"
      );
    },

    isRqrLeader() {
      return (
        this.review.rqr_leader_id === this.userId &&
        this.profile === "rqr_leader" &&
        this.review.status > 5 &&
        this.review.status < 10
      );
    },

    isRqrLeaderAnytime() {
      return (
        this.review.rqr_leader_id === this.userId && this.profile === "rqr_leader"
      );
    },

    questionsTotalForReviewer() {
      return this.questions.reduce((current, questionsSet) => {
        if (
          questionsSet.questions[0].section !== "manager" &&
          questionsSet.questions[0].section !== "engagement_leader" &&
          questionsSet.questions[0].section !== "manager_for_quality" &&
          questionsSet.questions[0].section !== "engagement_leader_for_quality" &&
          questionsSet.questions[0].section !== "manager_for_inFlight" &&
          questionsSet.questions[0].section !== "engagement_leader_for_inFlight"
        ) {
          return current + questionsSet.questions.length;
        } else {
          return current;
        }
      }, 0);
    },

    questionsAndRatingsTotalLeft() {
      const flattedQuestions = this.questions.reduce(
        (current, b) => current.concat(b["questions"]),
        []
      );
      let reviewerAnswers = this.review.answers.filter((answer) => {
        return flattedQuestions.find((question) => {
          if (
            answer["question_id"] === question["id"] &&
            question.section !== "manager" &&
            question.section !== "engagement_leader" &&
            question.section !== "manager_for_quality" &&
            question.section !== "engagement_leader_for_quality" &&
            question.section !== "manager_for_inFlight" &&
            question.section !== "engagement_leader_for_inFlight"
          ) {
            return answer;
          }
        });
      });
      return this.questionsTotalForReviewer - reviewerAnswers.length;
    },

    defaultActiveStep() {
      if (this.isAdmin || this.isLeader || this.isLeaderAnytime) {
        return "Engagement Leader";
      }

      if (this.isManager || this.isManagerAnytime) {
        return "Manager";
      }

      if (
        this.isReviewer ||
        this.isReviewerAnytime ||
        this.isRqrLeader ||
        this.isRqrLeaderAnytime
      ) {
        if (this.review.status >= 6) {
          return "Engagement Leader";
        } else {
          return this.review.advisory.rqr_type === 2
            ? "Review Comments"
            : "Clear scope";
        }
      }
    },

    disabledForReviewerAnyTime() {
      return (
        (!this.isAdmin && this.review.status === 10) ||
        (!this.isAdmin &&
          this.isReviewerAnytime &&
          this.review.status >= 7 &&
          !this.isRqrLeaderAnytime) ||
        (this.isAdminTI || this.isAudit)
      );
    },
  },

  data() {
    return {
      isFetching: false,
      isQuestionnaireFinished: false,

      isSending: false,
      showError: false,

      showingNotification: false,
      showingSchedule: false,
      showingReview: {},

      client_name: "",
      project: "",
      engagement_description: "",
      engagement_leader: "",
      manager_name: "",

      questions: [],
      review: {
        answer: {},
      },

      errorResponses: {
        400: "The review answer is invalid",
        422: "There is an error with your answers",
      },

      selectedFile: {
        showing: false,
        uploading: false,
        file: false,
        comment: "",
      },

      deletableFile: {
        trying: false,
        file: false,
        index: false,
        deleting: false,
      },

      showingFile: false,
      checked: false,

      status: false,
      sendingStatus: false,
      meetings: [
        { date: "", time: "" },
        { date: "", time: "" },
        { date: "", time: "" },
      ],
      isSendingScheduleMeet: false,

      statuses: [
        { id: 1, name: "Not started" },
        { id: 2, name: "Engagement Leader - Pending" },
        { id: 3, name: "Engagement Leader - Completed" },
        { id: 4, name: "Manager - Pending" },
        { id: 5, name: "Manager - Completed" },
        { id: 6, name: "Reviewer - Pending" },
        { id: 7, name: "Reviewer - Completed" },
        { id: 8, name: "EL to Confirm" },
        { id: 9, name: "RQR Leader - Pending" },
        { id: 10, name: "Finished" },
      ],
    };
  },

  methods: {

    questionError(question) {
      return this.showError ? question.answered : true
    },

    findAnswer(question_id, multiple = false) {
      if (multiple) {
        const answers = this.review.answers.filter(
          (ans) => ans.question_id === question_id
        );

        return answers || [];
      }

      const answer = this.review.answers.find((ans) => {
        return ans.question_id === question_id;
      });

      return answer || {};
    },

    findRating(section) {
      const rating = this.review.ratings.find((rat) => rat.section === section);

      return rating || {};
    },

    updateRating({ section, value }) {
      this.review.ratings.push({
        section: section,
        rating: value,
      });

      this.review.ratings.forEach((rat) => {
        if (rat.section === section) {
          rat.rating = value;
        }
      });
    },

    showFile(file) {
      if (this.showingFile) return;

      this.showingFile = true;

      this.$api.request
        .get(`files/${file.id}`)
        .then((response) => {
          const popup = window.open(response.data.url, "_blank");

          if (popup === null || typeof popup === "undefined") {
            this.$toast.show(
              "Oops! We noticed your pop ups are being blocked. Enable pop ups and try again."
            );
          } else {
            popup.focus();
          }
        })
        .catch(() => {
          this.$toast.show("Ops, something went wrong! Please, try again.");
        })
        .finally(() => {
          this.showingFile = false;
        });
    },

    onFileSelected(e) {
      this.selectedFile.file = e.target.files[0];
      this.selectedFile.showing = true;
    },

    sendFile() {
      const review_id = this.$route.params.id;
      const formData = new FormData();

      formData.append("file", this.selectedFile.file);
      formData.append("comment", this.selectedFile.comment);
      formData.append("review_id", review_id);

      this.selectedFile.uploading = true;
      this.$api.request
        .post("files", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          this.review.files.push(res.data);

          this.updateQuestionnaireComeBackToReviewPending();
        }).catch((error)=>{
          if(error.response.status == 422){
            this.$toast.error(error.response.data.message, "");
          }else{
            this.$toast.error("Unexpected error", "");
          }
        })
        .finally(() => {
          this.selectedFile.showing = false;
          this.selectedFile.uploading = false;
          this.selectedFile.file = false;
          this.selectedFile.comment = "";
        });
    },

    tryToDeleteFile(file, i) {
      this.deletableFile.trying = true;
      this.deletableFile.file = file;
      this.deletableFile.index = i;
    },

    deleteFile() {
      this.deletableFile.deleting = true;

      this.$api.request
        .delete("files/" + this.deletableFile.file.id)
        .then(() => {
          const idx = this.review.files.findIndex(
            (f) => f.id === this.deletableFile.file.id
          );
          if (idx >= 0) {
            this.$delete(this.review.files, idx);
          }

          this.updateQuestionnaireComeBackToReviewPending();
        })
        .finally(() => {
          this.deletableFile.trying = false;
          this.deletableFile.file = false;
          this.deletableFile.index = false;
          this.deletableFile.deleting = false;
        });
    },

    sendAll() {
      this.getReview()
        .then(() => {
          const notCompliant = this.review.ratings.filter(
            (r) => r.rating && r.rating > 1
          );

          const pending = notCompliant.filter(
            (r) => {
              const missingActionPlan = !r.action_plan || !r.action_plan_deadline;
              const notFinalSection = r.section !== "final";
              const isVisible = this.isVisible(r.section);

              return missingActionPlan && notFinalSection && isVisible;
            }
          );

          let flag = false;
          let sectionText = "";

          this.questions.forEach((question) => {
            const section = question.questions[0].section;
            const sectionTextName = question.questions[0].section_text;
            const isVisible = this.isVisible(section);

            if (isVisible) {
              const unansweredQuestions = question.questions.filter((q) => {
                const isEngagementOrManager = (q.section_text === 'Engagement Leader' || q.section_text === 'Manager');
                const shouldSkipForStatus6 = this.review.status == 6 && isEngagementOrManager;
                const isUnanswered = !q.answered;

                return shouldSkipForStatus6 ? false : isUnanswered;
              });

              if (unansweredQuestions.length > 0) {
                this.showError = true;
                flag = true;
                sectionText = sectionText + sectionTextName + " - ";
              }
            }
          });

          if (sectionText.length > 0) {
            sectionText = sectionText.substring(0, sectionText.length - 3);
          }

          if (flag) {
            this.$toast.show(
              `Pending answer some questions in the following sections: ${sectionText}`
            );
            return;
          }

          // Validar se ratings obrigatórios foram preenchidos (para Reviewer, RQR Leader e Admin)
          console.log(' Validando ratings obrigatórios...');
          if (this.isReviewer || this.isRqrLeader || this.isAdmin) {
            if (!this.validateRequiredRatings()) {
              return;
            }
          }

          if (pending.length > 0) {
            const message = pending
              .map((r) => {
                const displayName = this.getSectionDisplayName(r.section);
                return displayName;
              })
              .filter(name => !!name && name.trim() !== '') // Remove valores vazios
              .join(" - ");

            // Se a mensagem estiver vazia, criar uma mensagem genérica mais informativa
            if (!message || message.trim() === '') {
              this.$toast.show(
                `Pending action plan or deadline in the following sections: ${pending.map(p => p.section).join(', ')}`
              );
            } else {
              this.$toast.show(
                `Pending action plan or deadline in the following sections: ${message}`
              );
            }
            return;
          }

          console.log('✅ Todas as validações passaram - prosseguindo com salvamento');
          this.showError = false;

          const id = this.$route.params.id;
          let status = 0;

          if (this.isLeader) {
            status = 3;
          } else if (this.isManager) {
            status = 5;
          } else if (this.isReviewer || this.isRqrLeader) {
            status = this.review.status + 1;
          } else if (this.isAdmin) {
            status = this.review.status + 1;
          }

          this.isSending = true;

          this.$api.request
            .put(`reviews/${id}/status`, { status, beSend: 1 })
            .then(() => {
              this.review.status = status;

              this.isQuestionnaireFinished = true;
              this.isSending = false;
              this.$router.push({ name: 'Reviews', params: { id: 1 } });
            })
            .catch((error) => {
              if (this.errorResponses[error.response.status]) {
                this.$toast.show(this.errorResponses[error.response.status]);
              } else {
                this.$toast.show("Ops, something went wrong! Please, try again.");
              }
              this.isSending = false;
            })
        })
    },

    validateRequiredRatings() {
      return validateRequiredRatings(
        this.review,
        this.questions,
        this.isVisible.bind(this),
        this.$toast.show.bind(this.$toast)
      );
    },

    canSend() {
      return (
        this.isLeader ||
        this.isManager ||
        this.isReviewer ||
        this.isRqrLeader ||
        this.isAdmin
      );
    },

    findSection(questions) {
      return questions.questions[0].section;
    },

    getSectionDisplayName(sectionKey) {
      // Busca o section_text correto baseado na seção do rating
      for (const questionSet of this.questions) {
        if (questionSet.questions[0].section === sectionKey) {
          const displayName = questionSet.section_text || questionSet.questions[0].section_text;
          return displayName;
        }
      }

      // Fallback para seção final ou outras seções especiais
      if (sectionKey === 'final') {
        return 'Overall';
      }

      // Mapeamento de fallback para seções conhecidas
      const sectionMap = {
        'documentation': 'Documentation',
        'performance_and_review': 'Performance & Review',
        'technical_documentation': 'Technical Documentation',
        'testing_and_quality_assurance': 'Testing and Quality Assurance',
        'engagement_leader': 'Engagement Leader',
        'manager': 'Manager',
        'engagement_leader_for_quality': 'Engagement Leader',
        'manager_for_quality': 'Manager',
        'engagement_leader_for_inFlight': 'Engagement Leader',
        'manager_for_inFlight': 'Manager',
        // Adicionando o mapeamento específico para a seção problemática
        'documentation_for_quality_review_advisory': 'Documentation',
        'value_proposition': 'Value Proposition',
        'client': 'Client',
        'contract': 'Contract',
        'talent': 'Talent',
        'delivery': 'Delivery',
        'finance': 'Finance'
      };

      const mappedName = sectionMap[sectionKey];
      if (mappedName) {
        return mappedName;
      }

      return sectionKey; // Retorna o nome mapeado ou a chave original
    },

    changeStepNumber() {
      setTimeout(() => {
        const numbers = document.querySelectorAll(".md-stepper-number");
        numbers.forEach((number) => {
          number.innerHTML = `
              <i class="md-icon md-icon-font md-icon-image md-stepper-editable md-theme-default">
                <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                  <path d="M0 0h24v24H0z" fill="none"></path>
                </svg>
              </i>
            `;
        });
      }, 500);
    },

    postSent(payload) {
      this.incrementQuestionsAnswered(payload);
      this.updateQuestionnaireComeBackToReviewPending();

      // Atualizar a resposta nos dados locais da review
      if (payload.answerData) {
        this.updateLocalAnswer(payload.question, payload.answerData);
      }

      // Atualizar o estado das questões após uma resposta ser enviada
      this.updateQuestionAnsweredStatus();
    },

    updateLocalAnswer(questionId, answerData) {
      // Encontrar e atualizar a resposta existente ou adicionar nova
      const existingAnswerIndex = this.review.answers.findIndex(
        (a) => a.question_id === questionId
      );

      if (existingAnswerIndex >= 0) {
        // Se a resposta está vazia, remover do array
        if (!answerData.answer && !answerData.option_id && (!answerData.files || answerData.files.length === 0)) {
          this.review.answers.splice(existingAnswerIndex, 1);
        } else {
          // Atualizar resposta existente
          this.review.answers[existingAnswerIndex] = {
            ...this.review.answers[existingAnswerIndex],
            ...answerData
          };
        }
      } else if (answerData.answer || answerData.option_id || (answerData.files && answerData.files.length > 0)) {
        // Adicionar nova resposta apenas se não estiver vazia
        this.review.answers.push(answerData);
      }
    },

    updateQuestionAnsweredStatus() {
      this.questions.forEach((question) => {
        question.questions.forEach((q) => {
          // Verificar se existe uma resposta válida para esta questão
          const hasValidAnswer = this.review.answers.some((a) => {
            if (a.question_id === q.id) {
              // Considerar resposta válida se tem conteúdo não vazio
              return a.answer || a.option_id || (a.files && a.files.length > 0);
            }
            return false;
          });
          q.answered = hasValidAnswer;
        });
      });
    },

    incrementQuestionsAnswered(payload) {
      if (payload.question !== undefined) {
        if (
          this.review.answers.find(
            (answer) => answer["question_id"] === payload.question
          ) === undefined
        ) {
          this.review.answers.push({ question_id: payload.question });
        }
      }
    },

    updateQuestionnaireComeBackToReviewPending() {
      if (this.review.status > 6 && this.review.status < 10) {
        this.$api.request
          .put(`reviews/${this.review.id}/status`, { status: 6 })
          .then(() => {
            this.review.status = 6;

            this.$toast.show(
              "The review status has changed. An email notification was sent."
            );
          });
      }
    },

    makeSectionsClosable(e) {
      let stepperButtonEl = e.target.closest("button.md-button");

      if (stepperButtonEl.classList.contains("md-closed")) {
        stepperButtonEl.classList.add("md-active");
        stepperButtonEl.nextElementSibling.classList.add("md-active");

        stepperButtonEl.classList.remove("md-closed");

        return;
      }

      if (stepperButtonEl.classList.contains("md-active")) {
        stepperButtonEl.classList.remove("md-active");
        stepperButtonEl.nextElementSibling.classList.remove("md-active");

        stepperButtonEl.classList.add("md-closed");
      }
    },

    setStepPosition() {
      const stepper = this.$refs["answersStepper"];
      const activeStepIndex = stepper.getStepperNumber(stepper.activeIndex);
      const stepperEl = stepper.$el.querySelector(
        ".md-stepper:nth-child(" + activeStepIndex + ")"
      );

      if (stepperEl) {
        this.$nextTick(() => {
          window.scrollTo(
            0,
            Math.abs(window.document.body.getBoundingClientRect().top) +
            stepperEl.getBoundingClientRect().top -
            15
          );
        });
      }
    },

    isDisabledForReviewer(question) {
      const reviewerReadOnlySections = {
        engagement_leader: "engagement_leader",
        manager: "manager",
        engagement_leader_for_quality: "engagement_leader_for_quality",
        manager_for_quality: "manager_for_quality",
        engagement_leader_for_inFlight: "engagement_leader_for_inFlight",
        manager_for_inFlight: "manager_for_inFlight",
      };

      return !this.isAdmin &&
        !this.isLeader &&
        !this.isManager &&
        reviewerReadOnlySections[question["section"]]
        ? true
        : false;
    },

    checkUserAccess() {
      const userId = this.userId;
      const review = this.review;

      // Admin sempre tem acesso
      if (this.isAdmin) {
        return true;
      }

      // Verificar se tem algum papel na review
      const hasRole = (
        review.engagement_leader_id === userId ||
        review.manager_engagement_id === userId ||
        review.rqr_reviewer_id === userId ||
        review.rqr_leader_id === userId
      );

      return hasRole;
    },

    getReview() {
      console.log('Answer.vue getReview() - Loading review ID:', this.$route.params.id);
      return this.$api
        .request("reviews/" + this.$route.params.id)
        .then((reviewResponse) => {
          this.review = camelToSnake(reviewResponse.data[0]);

          // Verificação de acesso - abordagem simples
          const hasAccess = this.checkUserAccess();
          if (!hasAccess) {
            console.log('Answer.vue getReview() - User does not have access to this review');
            this.$toast.error('You do not have permission to access this review.');
            this.$router.push({ name: 'Dashboard' });
            return;
          }

          //Removendo answers nulas
          this.review.answers = this.review.answers.filter(answer => !(answer.answer === null && answer.option_id === null && answer.files.length === 0));
          const queryString = `rqrType=${this.review.advisory.rqr_type}&questionnaireId=${this.review.advisory.questionnaire_id}`;
          this.$api
            .request(`questions-for-questionnaire?${queryString}`)
            .then((questionsResponse) => {
              this.questions = camelToSnake(questionsResponse.data).map((question) => {
                let improvedQuestion = question;

                if (question.questions[0].section_description) {
                  improvedQuestion.section_description =
                    question.questions[0].section_description;
                }
                improvedQuestion.questions.forEach((q) => {
                  q.answered = false;
                  if (
                    this.review.answers.filter((a) => a.question_id === q.id)
                      .length > 0
                  ) {
                    q.answered = true;
                  }
                });
                return improvedQuestion;
              });

              this.client_name = this.review.client_name;
              this.project = this.review.job_code;
              this.engagement_description = this.review.engagement_description;

              this.$api
                .request("users/" + this.review.manager_engagement_id)
                .then((usersResponse) => {
                  this.manager_name = usersResponse.data.name;
                });

              this.$api
                .request("users/" + this.review.engagement_leader_id)
                .then((usersResponse) => {
                  this.engagement_leader = usersResponse.data.name;
                });
            })
            .finally(() => {
              this.isFetching = false;
              this.changeStepNumber();
            });
        })
        .catch((error) => {
          console.error('Answer.vue getReview() - Error loading review:', error);
          this.isFetching = false;

          // Se erro 403 ou 404, provavelmente sem acesso
          if (error.response && (error.response.status === 403 || error.response.status === 404)) {
            this.$toast.error('Review not found or you do not have permission to access it.');
          } else {
            this.$toast.error('Error loading review. Please try again.');
          }

          this.$router.push({ name: 'Dashboard' });
        });
    },

    isVisible(section) {
      if (
        this.isAdmin ||
        this.isAudit ||
        this.isAdminTI ||
        this.isRqrLeader ||
        (section === "engagement_leader" && this.isLeader) ||
        (section === "engagement_leader_for_quality" && this.isLeader) ||
        (section === "engagement_leader_for_inFlight" && this.isLeader) ||
        (section === "manager" && this.isManager) ||
        (section === "manager_for_quality" && this.isManager) ||
        (section === "manager_for_inFlight" && this.isManager) ||
        (section !== "manager" &&
          section !== "engagement_leader" &&
          this.isReviewer) ||
        (section !== "manager_for_quality" &&
          section !== "engagement_leader_for_quality" &&
          this.isReviewer) ||
        (this.isLeaderAnytime && section === "engagement_leader") ||
        (this.isLeaderAnytime && section === "engagement_leader_for_quality") ||
        (this.isLeaderAnytime && section === "engagement_leader_for_inFlight") ||
        (this.isManagerAnytime && section === "manager") ||
        (this.isManagerAnytime && section === "manager_for_quality") ||
        (this.isManagerAnytime && section === "manager_for_inFlight") ||
        (this.isRqrLeaderAnytime &&
          this.review.status > 5 &&
          this.review.status <= 10) ||
        (this.isReviewerAnytime &&
          this.review.status > 5 &&
          this.review.status <= 10) ||
        this.isRqrLeader
      ) {
        return true;
      }
      return false;
    },
  },

  created() {
    this.isFetching = true;
    this.getReview();
    const profile = this.$route.query.profile;

    if (profile) {
      if (
        (profile !== "admin" && profile === "manager") ||
        profile === "reviewer" ||
        profile === "rqr_leader" ||
        profile === "engagement_leader"
      ) {
        this.$store.commit("CURRENTPROFILE", profile);
      }
    }
  },
  watch: {
    "$store.state.currentProfile": function () {
      this.changeStepNumber();
    },
  },
};
</script>
